import React from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  VideoLibrary as VideoIcon,
  LibraryMusic as MusicIcon,
  People as PeopleIcon,
  AudioFile as AudioIcon,
  Book as BookIcon,
  SelfImprovement as MeditationIcon,
  Settings as SettingsIcon,
  LocalOffer as PromocodesIcon,
  MenuBook as DocumentationIcon,
  Language as LanguageIcon,
  Translate as TranslateIcon,
  AutoStories as TalesIcon,
  MusicNote as LullabyIcon,
  MarkEmailRead as WhitelistIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 280;

interface SidebarItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  badge?: string;
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const menuItems: SidebarItem[] = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Videos', icon: <VideoIcon />, path: '/videos' },
    { text: 'Songs', icon: <MusicIcon />, path: '/songs' },
    { text: 'Audios', icon: <AudioIcon />, path: '/audios' },
    { text: 'Books', icon: <BookIcon />, path: '/books' },
    { text: 'Meditations', icon: <MeditationIcon />, path: '/meditations' },
    { text: 'Tales', icon: <TalesIcon />, path: '/tales' },
    { text: 'Lullabies', icon: <LullabyIcon />, path: '/lullabies' },
  ];

  const managementItems: SidebarItem[] = [
    { text: 'App Users', icon: <PeopleIcon />, path: '/users' },
    { text: 'Promocodes', icon: <PromocodesIcon />, path: '/promocodes' },
    ...(userRole === 'admin'
      ? [
          { text: 'Auth Users', icon: <PeopleIcon />, path: '/auth-users', badge: 'Admin' },
          {
            text: 'Whitelist Verify Emails',
            icon: <WhitelistIcon />,
            path: '/whitelist-verify-emails',
            badge: 'Admin',
          },
        ]
      : []),
  ];

  const documentationItems: SidebarItem[] = [
    { text: 'Documentation', icon: <DocumentationIcon />, path: '/documentation' },
  ];

  const localizationItems: SidebarItem[] = [
    { text: 'Languages', icon: <LanguageIcon />, path: '/languages' },
    { text: 'Translations', icon: <TranslateIcon />, path: '/language-translations' },
  ];

  const settingsItems: SidebarItem[] = [
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  const handleItemClick = (path: string) => {
    navigate(path);
  };

  const isSelected = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      <Toolbar />

      <Box sx={{ px: 2, py: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Content Management
        </Typography>
      </Box>

      <List>
        {menuItems.map(item => (
          <ListItemButton
            key={item.text}
            selected={isSelected(item.path)}
            onClick={() => handleItemClick(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              slotProps={{
                primary: {
                  fontSize: '0.875rem',
                  fontWeight: isSelected(item.path) ? 600 : 400,
                },
              }}
            />
            {item.badge && (
              <Chip
                label={item.badge}
                size="small"
                color="success"
                sx={{
                  height: 20,
                  fontSize: '0.625rem',
                  fontWeight: 600,
                }}
              />
            )}
          </ListItemButton>
        ))}
      </List>

      <Divider sx={{ mx: 2, my: 2 }} />

      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="overline" color="text.secondary">
          Localization
        </Typography>
      </Box>

      <List>
        {localizationItems.map(item => (
          <ListItemButton
            key={item.text}
            selected={isSelected(item.path)}
            onClick={() => handleItemClick(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              slotProps={{
                primary: {
                  fontSize: '0.875rem',
                  fontWeight: isSelected(item.path) ? 600 : 400,
                },
              }}
            />
          </ListItemButton>
        ))}
      </List>

      <Divider sx={{ mx: 2, my: 2 }} />

      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="overline" color="text.secondary">
          User Management
        </Typography>
      </Box>

      <List>
        {managementItems.map(item => (
          <ListItemButton
            key={item.text}
            selected={isSelected(item.path)}
            onClick={() => handleItemClick(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              slotProps={{
                primary: {
                  fontSize: '0.875rem',
                  fontWeight: isSelected(item.path) ? 600 : 400,
                },
              }}
            />
            {item.badge && (
              <Chip
                label={item.badge}
                size="small"
                color="success"
                sx={{
                  height: 20,
                  fontSize: '0.625rem',
                  fontWeight: 600,
                }}
              />
            )}
          </ListItemButton>
        ))}
      </List>

      <Divider sx={{ mx: 2, my: 2 }} />

      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="overline" color="text.secondary">
          Documentation
        </Typography>
      </Box>

      <List>
        {documentationItems.map(item => (
          <ListItemButton
            key={item.text}
            selected={isSelected(item.path)}
            onClick={() => handleItemClick(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              slotProps={{
                primary: {
                  fontSize: '0.875rem',
                  fontWeight: isSelected(item.path) ? 600 : 400,
                },
              }}
            />
          </ListItemButton>
        ))}
      </List>

      <Divider sx={{ mx: 2, my: 2 }} />

      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="overline" color="text.secondary">
          System
        </Typography>
      </Box>

      <List>
        {settingsItems.map(item => (
          <ListItemButton
            key={item.text}
            selected={isSelected(item.path)}
            onClick={() => handleItemClick(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              slotProps={{
                primary: {
                  fontSize: '0.875rem',
                  fontWeight: isSelected(item.path) ? 600 : 400,
                },
              }}
            />
          </ListItemButton>
        ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ p: 2, m: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          Todly Admin Panel v1.0
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          © 2025 Gamester
        </Typography>
      </Box>
    </Drawer>
  );
}
